import axios from "axios";
import { API_BASE_PATH, API_PATH } from "../utils/config";
import {
  buildExpiryLoginRedirect,
  readAuthStore,
} from "../utils/loginRedirect";
import {
  isEmailUnverifiedError,
  showEmailUnverifiedCue,
} from "../utils/emailUnverifiedCue";
import { v4 as uuidv4 } from "uuid"; // Importing uuid package

const getBaseUrl = () => {
  let baseUrl = API_BASE_PATH;
  if (
    typeof process !== "undefined" &&
    process?.env?.NODE_ENV === "development"
  ) {
    baseUrl = API_BASE_PATH;
  }
  return baseUrl;
};

const instance = axios.create({
  baseURL: getBaseUrl(),
});

// ISP-9999 (gordon-iphone-05-verify-email-spam-and-kickback): verify_email and
// resend_verification are anonymous, AllowAny endpoints that don't need (or
// use) an auth token -- but MEASURED live on app-stg 2026-08-31, attaching
// ANY stale/deleted token still in localStorage flips their response from a
// clean 400 ("Invalid or expired verification key") to a 401
// `{"detail":"Invalid token."}` (SoftDeleteAwareTokenAuthentication runs
// BEFORE the view's AllowAny permission check and rejects a *supplied*
// invalid credential outright, regardless of whether one was required). The
// response interceptor below treats that exact 401 as "session expired" and
// force-clears localStorage + hard-redirects to /login -- so a visitor whose
// browser happens to hold an old/invalidated token (e.g. a prior session on
// the same device) gets kicked back to the login screen by clicking their
// verification link, instead of being verified (and, if they were already
// signed in via non-blocking login, instead of staying signed in).
const ANONYMOUS_CALLS = [
  API_PATH.LOGIN,
  API_PATH.VERIFY_EMAIL_LINK,
  API_PATH.RESEND_VERIFICATION_LINK,
];

const notLoginCall = (config) => {
  return !ANONYMOUS_CALLS.includes(config.url);
};

// ── In-flight GET de-duplication (ISP-9999) ─────────────────────────────────
// A single cold load of /gallery/21 fired six genuinely-redundant IDENTICAL
// backend GETs (profile x3, wheel/created x2, workspaces x2, packages/active x2,
// dm/conversations x2). The duplicates come from independent call sites hitting
// the same URL through DIFFERENT layers — a redux thunk (raw axios.get) and a
// react-query hook (inlined API.get) — so react-query's own in-flight de-dup can
// never collapse them. The one layer both paths share is the transport, keyed by
// URL, so the coalescer lives here.
//
// Scope, deliberately narrow (behaviour-preserving):
//   • GET only — mutations (POST/PUT/PATCH/DELETE) are never touched.
//   • IN-FLIGHT only — the entry is evicted the instant the request settles, so
//     this is pure concurrent de-dup, never a cache. A sequential repeat still
//     hits the network; no staleness is introduced and no data changes.
//   • Each caller receives an INDEPENDENT response envelope (top-level `data`
//     shallow-cloned), so a legacy caller mutating its copy cannot corrupt a
//     coalesced sibling — matching the pre-existing "one fresh response per call"
//     semantics rather than sharing a single mutable object.
//   • Opt-out: pass `{ dedupe: false }` in the request config to bypass.
const inFlightGets = new Map();

const dedupeKey = (url, config) => {
  let paramStr = "";
  const params = config && config.params;
  if (params) {
    try {
      if (typeof params.toString === "function" && !(params instanceof Object && params.constructor === Object)) {
        // URLSearchParams / string-like
        paramStr = params.toString();
      } else {
        paramStr = JSON.stringify(
          Object.keys(params)
            .sort()
            .reduce((acc, k) => {
              acc[k] = params[k];
              return acc;
            }, {})
        );
      }
    } catch (_) {
      paramStr = "";
    }
  }
  return `${url}?${paramStr}`;
};

const cloneResponse = (res) => {
  if (!res || typeof res !== "object") return res;
  const data = res.data;
  let clonedData = data;
  if (Array.isArray(data)) clonedData = data.slice();
  // Only plain JSON objects are shallow-cloned: spreading a Blob/ArrayBuffer
  // (responseType "blob", e.g. the Responses CSV export) yields `{}`, which the
  // caller then saved as a 15-byte "[object Object]" file (T28.3).
  else if (
    data &&
    typeof data === "object" &&
    [Object.prototype, null].includes(Object.getPrototypeOf(data))
  )
    clonedData = { ...data };
  return { ...res, data: clonedData };
};

const originalGet = instance.get.bind(instance);
instance.get = function dedupedGet(url, config = {}) {
  if (config && config.dedupe === false) return originalGet(url, config);

  const key = dedupeKey(url, config);
  if (inFlightGets.has(key)) {
    // Coalesce onto the shared in-flight request; hand back an independent copy.
    return inFlightGets.get(key).then(cloneResponse);
  }

  const shared = originalGet(url, config);
  // Evict on settle (success OR failure) so this is strictly in-flight de-dup.
  shared.then(
    () => inFlightGets.delete(key),
    () => inFlightGets.delete(key)
  );
  inFlightGets.set(key, shared);
  return shared.then(cloneResponse);
};

instance.interceptors.request.use(
  (request) => {
    // console.log(request)
    if (!request.headers.Authorization) {
      const token = JSON.parse(localStorage.getItem("keyCloak") || "{}").token;
      if (token) {
        request.headers.Authorization = `Token ${token}`;
      }
    }

    //  Add TRANSACTION-ID header with a random number
    const transactionId = uuidv4(); // Generate a unique UUID
    request.headers["TRANSACTION-ID"] = transactionId;

    return request;
  },
  (error) => {
    console.log(error);
    return Promise.reject(error);
  },
  { runWhen: notLoginCall }
);

instance.interceptors.response.use(
  (response) => {
    if (
      response?.config?.url === API_PATH.LOGIN ||
      response?.config?.url === API_PATH.GOOGLE_LOGIN ||
      response?.config?.url === API_PATH.FACEBOOK_LOGIN ||
      response?.config?.url === API_PATH.APPLE_LOGIN ||
      response?.config?.url === API_PATH.MAGIC_LINK_REDEEM ||
      // FEAT-L9: passkey authentication_verify returns the SAME {token, user}
      // shape as password/magic-link login (see backend
      // PasskeyAuthenticationVerifyViewSet doc comment) -- persist it here too.
      response?.config?.url === API_PATH.PASSKEY_AUTHENTICATION_VERIFY
    ) {
      let token = response?.data?.token;
      let user = response?.data?.user || JSON.parse(localStorage.getItem("keyCloak")).user;
      localStorage.setItem("keyCloak", JSON.stringify({ token, user }));
    }
    // console.log(response)
    return response;
  },
  (error) => {
    console.log("error", error);
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data.detail === "Invalid token."
    ) {
      // BUG #700 generic mid-session expiry (Gordon 2026-08-23): capture WHO was
      // signed in BEFORE clearing the store. A 401 "Invalid token." means a
      // token WAS sent and the server rejected it — i.e. a session that was
      // authenticated has just expired mid-use — so we (almost always) know the
      // user's email here. Passing it to buildExpiryLoginRedirect routes the
      // user to the explained re-auth banner (reason=session-expired + email
      // prefill) instead of a bare /login with no explanation. If no email is
      // remembered, the helper falls back to the same return-to login URL this
      // block used before (byte-identical for that case).
      const expiredEmail = readAuthStore().user?.email || "";
      localStorage.removeItem("keyCloak");
      localStorage.removeItem("persist:root"); // EW-1004: clear persisted redux remnant on forced logout
      window.location.href =
        window.location.origin + buildExpiryLoginRedirect(expiredEmail);
    }
    // error.response.status === 403 && error.response.data.detail === "CSRF Failed: CSRF token missing or incorrect."
    // ISP-9999 fe-unverified-banner-and-cues: every gated action (purchase,
    // publish/share, chat send, invite, email change, referral, username set,
    // clone) shares this one axios instance, so this is the single place that
    // catches the backend's 403 email_unverified refusal and shows a visible
    // cue instead of the action just failing silently. Behind
    // UNVERIFIED_GUARDRAILS_ENABLED only in spirit — inert in practice while
    // the backend flag is off, since that 403 code is never returned then.
    if (isEmailUnverifiedError(error)) {
      showEmailUnverifiedCue();
    }
    return Promise.reject(error);
  }
);

export default instance;
